'use client'

import { useEffect, useId, useRef, useState } from 'react'
import clsx from 'clsx'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

import ChevronDownSmall from '/react4xp/components/prototype/icons/ChevronDownSmall'
import SelectedDot from '/react4xp/components/prototype/icons/SelectedDot'

import type { LanguageSelectorProps } from './types'

export default function LanguageSelector({
	options = [],
	active,
	onSelect = () => {},
	label = 'Language',
}: LanguageSelectorProps) {
	const [isOpen, setIsOpen] = useState(false)
	const panelId = useId()
	const wrapperRef = useRef<HTMLDivElement>(null)
	const rootRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const firstOptionRef = useRef<HTMLButtonElement>(null)

	const activeOption = options.find((option) => option.value === active) ?? options[0]

	function close() {
		setIsOpen(false)
		triggerRef.current?.focus()
	}

	function toggle() {
		setIsOpen((prev) => !prev)
	}

	function handleSelect(value: string) {
		onSelect(value)
		close()
	}

	// height animation: box grows in-flow from h-11 (44px) to h-32 (128px), not an overlay
	useGSAP(
		() => {
			const el = wrapperRef.current
			if (!el) return
			gsap.killTweensOf(el)

			if (isOpen) {
				gsap.set(el, { height: 'auto' })
				const target = el.offsetHeight
				gsap.fromTo(
					el,
					{ height: '2.75rem' },
					{
						height: target,
						duration: 0.3,
						ease: 'power2.inOut',
						onComplete: () => gsap.set(el, { height: 'auto' }),
					},
				)
			} else {
				gsap.to(el, {
					height: '2.75rem',
					duration: 0.3,
					ease: 'power2.inOut',
				})
			}
		},
		{ dependencies: [isOpen], scope: rootRef },
	)

	useEffect(() => {
		if (isOpen) firstOptionRef.current?.focus()
	}, [isOpen])

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === 'Escape' && isOpen) close()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen])

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (isOpen && rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	return (
		<div ref={rootRef} className="z-1000 inline-block h-11 overflow-visible">
			<div
				ref={wrapperRef}
				className={clsx(
					'flex h-11 w-16.5 flex-col gap-1 overflow-hidden rounded-xs border bg-black-950 transition-colors',
					isOpen ? 'border-brand-300' : 'border-brand-500 hover:border-brand-300',
				)}
			>
				<button
					ref={triggerRef}
					type="button"
					aria-expanded={isOpen}
					aria-controls={panelId}
					aria-label={label}
					onClick={toggle}
					className="flex shrink-0 items-center justify-between gap-1 pl-3.5 pr-3 py-2 cursor-pointer"
				>
					<span className="text-base leading-normal font-normal text-pure-white">{activeOption?.label}</span>
					<ChevronDownSmall
						className={clsx('size-3 shrink-0 text-pure-white transition-transform', isOpen && 'rotate-180')}
						aria-hidden="true"
					/>
				</button>

				<ul
					id={panelId}
					role="listbox"
					aria-label={label}
					aria-hidden={!isOpen}
					// React 18 does not pass through the `inert` boolean prop; render the bare
					// attribute via an empty string when closed so the hidden panel is truly inert
					// (non-focusable), satisfying the aria-hidden-focus a11y rule.
					{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
					className="flex flex-col gap-1 pb-2 px-1.5"
				>
					{options.map((option, index) => {
						const isSelected = option.value === activeOption?.value
						return (
							<li key={option.value} className="flex">
								<button
									ref={index === 0 ? firstOptionRef : undefined}
									type="button"
									role="option"
									aria-selected={isSelected}
									onClick={() => handleSelect(option.value)}
									className={clsx(
										'flex w-13.5 h-6 shrink-0 items-center gap-1 rounded-full px-2 text-base leading-normal font-normal text-pure-white cursor-pointer',
										isSelected ? 'bg-brand-900' : 'bg-transparent hover:bg-brand-800',
									)}
								>
									{isSelected && <SelectedDot className="w-2 h-3.75 shrink-0" aria-hidden="true" />}
									{option.label}
								</button>
							</li>
						)
					})}
				</ul>
			</div>
		</div>
	)
}
